/**
 * Daily Bonus Modal Component
 * Shows a beautiful modal when daily bonus is available
 */
import React, { useState, useContext } from 'react';
import axios from 'axios';
import { AuthContext } from '../../WebApp';

function DailyBonusModal({ onClose, onClaimSuccess }) {
  const { user, updateUser, language } = useContext(AuthContext);
  const [claiming, setClaiming] = useState(false);
  const [error, setError] = useState(null);

  const translations = {
    en: {
      title: 'Daily Bonus Available!',
      message: 'We\'re glad to see you! You\'ve received free tokens. Come back tomorrow to get more!',
      claimButton: 'Claim It!',
      claiming: 'Claiming...',
      close: 'Close',
      success: 'Successfully claimed 5 tokens!',
      error: 'Failed to claim bonus. Please try again.'
    },
    ru: {
      title: 'Ежедневный бонус доступен!',
      message: 'Мы рады видеть вас! Вы получили бесплатные токены. Возвращайтесь завтра, чтобы получить еще!',
      claimButton: 'Забрать!',
      claiming: 'Забираем...',
      close: 'Закрыть',
      success: 'Успешно получено 5 токенов!',
      error: 'Не удалось получить бонус. Попробуйте еще раз.'
    }
  };

  const t = translations[language] || translations.en;

  const handleClaim = async () => {
    setClaiming(true);
    setError(null);
    
    try {
      const response = await axios.post('/web/daily-bonus/claim');
      
      if (response.data.success) {
        // Update user balance in context
        if (updateUser && user) {
          updateUser({
            ...user,
            token_balance: response.data.new_balance
          });
        }
        
        // Show success message briefly, then close
        setTimeout(() => {
          if (onClaimSuccess) {
            onClaimSuccess(response.data);
          }
          onClose();
        }, 1500);
      }
    } catch (err) {
      console.error('Failed to claim daily bonus:', err);
      setError(err.response?.data?.detail || t.error);
      setClaiming(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[9999] bg-black/80 backdrop-blur-sm flex items-center justify-center px-4 animate-fadeIn">
      <div className="bg-gradient-to-br from-dark-800 via-dark-900 to-burgundy-900 border-2 border-burgundy-600 rounded-3xl shadow-2xl max-w-lg w-full overflow-hidden animate-scaleIn">
        {/* Decorative header with image area */}
        <div className="relative bg-gradient-to-r from-burgundy-600 to-burgundy-800 p-8 text-center">
          <div className="absolute inset-0 opacity-20">
            <div className="w-full h-full bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZGVmcz48cGF0dGVybiBpZD0iZ3JpZCIgd2lkdGg9IjIwIiBoZWlnaHQ9IjIwIiBwYXR0ZXJuVW5pdHM9InVzZXJTcGFjZU9uVXNlIj48cGF0aCBkPSJNIDIwIDAgTCAwIDAgMCAyMCIgZmlsbD0ibm9uZSIgc3Ryb2tlPSIjZmZmIiBzdHJva2Utd2lkdGg9IjEiIG9wYWNpdHk9IjAuMSIvPjwvcGF0dGVybj48L2RlZnM+PHJlY3Qgd2lkdGg9IjEwMCIgaGVpZ2h0PSIxMDAiIGZpbGw9InVybCgjZ3JpZCkiLz48L3N2Zz4=')]"></div>
          </div>
          
          {/* Gift/Star Icon */}
          <div className="relative z-10">
            <div className="inline-block text-6xl mb-4 animate-bounce">
              🎁
            </div>
            <h2 className="text-3xl font-bold text-white mb-2 drop-shadow-lg">
              {t.title}
            </h2>
          </div>
        </div>

        {/* Content */}
        <div className="p-8 space-y-6">
          {/* Message */}
          <div className="text-center">
            <p className="text-lg text-gray-200 leading-relaxed">
              {t.message}
            </p>
            <div className="mt-4 flex items-center justify-center gap-2 text-2xl font-bold text-burgundy-400">
              <span>+5</span>
              <span className="text-lg text-gray-300">tokens</span>
            </div>
          </div>

          {/* Error message */}
          {error && (
            <div className="bg-red-900/30 border border-red-600/30 text-red-300 px-4 py-3 rounded-lg text-sm">
              {error}
            </div>
          )}

          {/* Buttons */}
          <div className="flex items-center gap-4 pt-2">
            <button
              type="button"
              onClick={onClose}
              disabled={claiming}
              className="flex-1 px-6 py-3 rounded-xl border-2 border-gray-600 text-gray-300 hover:bg-gray-700/50 hover:border-gray-500 transition-all font-semibold disabled:opacity-50"
            >
              {t.close}
            </button>
            <button
              type="button"
              onClick={handleClaim}
              disabled={claiming}
              className="flex-1 px-6 py-3 rounded-xl bg-gradient-to-r from-burgundy-600 to-burgundy-700 hover:from-burgundy-500 hover:to-burgundy-600 text-white font-bold text-lg shadow-lg hover:shadow-xl transform hover:scale-105 transition-all disabled:opacity-60 disabled:transform-none"
            >
              {claiming ? (
                <span className="flex items-center justify-center gap-2">
                  <span className="animate-spin">⏳</span>
                  {t.claiming}
                </span>
              ) : (
                <span className="flex items-center justify-center gap-2">
                  {t.claimButton}
                  <span>✨</span>
                </span>
              )}
            </button>
          </div>
        </div>

        {/* Decorative bottom border */}
        <div className="h-1 bg-gradient-to-r from-burgundy-600 via-burgundy-700 to-burgundy-600"></div>
      </div>

      <style>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }
        @keyframes scaleIn {
          from {
            transform: scale(0.9);
            opacity: 0;
          }
          to {
            transform: scale(1);
            opacity: 1;
          }
        }
        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out;
        }
        .animate-scaleIn {
          animation: scaleIn 0.3s ease-out;
        }
      `}</style>
    </div>
  );
}

export default DailyBonusModal;
